/**
 * `<caos-data-table>` — a sortable, selectable, column-configurable table of records.
 * (Catalog: Data display › DataTable — the TABLE body of a list surface — a row per record, ordered by its column headings.)
 *
 * A real `role="grid"` table whose columns and rows come in as DATA (properties, never
 * queried — the access-filtered result set is assembled by the app layer). Columns carry
 * a header label and an optional `sortable` flag: a sortable header is a real `<button>`
 * that toggles `aria-sort` ascending/descending and emits a bubbling, composed
 * `caos-sort` (`{ column, direction }`) — the host owns the actual re-ordering (no
 * internal data sort). Optional row selection adds a checkbox column with a tri-state
 * select-all in the header (`aria-selected` on each `<tr>`), composing the AF2
 * `caos-checkbox-field`. Cells hold a primitive (rendered as text) OR a slotted AF1–AF3
 * leaf (`{ slot }` → a named `<slot>`, e.g. a `caos-status-pill` in a status column) OR a
 * caller-built `node`. A row flagged `total` renders as a summary row — an accent top
 * rule with a bold display figure (a summary table's total row), carrying no
 * checkbox and excluded from selection. `loading` renders skeleton rows (composing
 * `caos-skeleton-block`); an empty result set renders `caos-empty-state`. A `footer`
 * slot carries the pagination affordance.
 *
 * Keyboard: focus roams the grid's interactive leaves in two dimensions — Arrow
 * Up/Down move between rows, Arrow Left/Right within a row, Home/End to the row ends —
 * enumerated through the shared shadow-piercing focus util (`collectFocusables` /
 * `deepActiveElement`), so a slotted leaf's real control (whose focusable element lives
 * in its own shadow root) is reached in order. The table scrolls inside its own
 * `overflow-x` container, so a wide table never widens the page.
 *
 * A column marked `editable` (CAOS-1672) is EDITED IN THE GRID: its cells draw a control over the
 * cell-edit presentation this component ships, and the table emits `caos-cell-edit` as a value
 * changes and `caos-cell-commit` when one is committed (Enter, or leaving the cell; Escape puts the
 * declared value back). The host owns the write, exactly as it owns the sort — the table reports the
 * edit and never changes a row. A cell that brings its own `{ node }` or `{ slot }` keeps it, so the
 * declared flag and the hand-composed editor coexist; total rows and disabled rows are never
 * editable. Declaring the affordance is not granting it: whether the write lands is the write
 * boundary's decision, unchanged.
 *
 * Styling seams: `::part(scroll)`, `::part(table)`, `::part(head)`, `::part(header-row)`,
 * `::part(header-cell)`, `::part(sort-button)`, `::part(select-all)`, `::part(body)`,
 * `::part(row)`, `::part(cell)`, `::part(cell-edit)`, `::part(row-select)`, `::part(footer)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';
import { collectFocusables, deepActiveElement } from '../focus.js';
import {
  DEFAULT_OVERSCAN,
  virtualWindow,
  wholeList,
  windowContaining,
  type VirtualWindow,
  type VirtualWindowInput,
} from '../virtual-window.js';
import './checkbox-field.js';
import './skeleton-block.js';
import './empty-state.js';
import type { CaosCheckboxField } from './checkbox-field.js';
import { makeIcon } from './icons.js';

export type CaosTableAlign = 'start' | 'center' | 'end';

export interface CaosTableColumn {
  /** The row-cell key this column reads. */
  key: string;
  /** The visible header label. */
  label: string;
  /** When true, the header is a sort toggle that reflects `aria-sort` + emits `caos-sort`. */
  sortable?: boolean;
  /** Horizontal alignment of the header + cells (numeric columns read `end`). */
  align?: CaosTableAlign;
  /** Optional fixed column width (any CSS length). */
  width?: string;
  /**
   * CAOS-1672 — when true, this column's cells are EDITED IN THE GRID: each one draws a control
   * over the cell-edit presentation this component already ships, and the table emits
   * `caos-cell-edit` as the value changes and `caos-cell-commit` when it is committed.
   *
   * The table does not own the value, exactly as it does not own the sort: it reports the edit and
   * the host writes it. Nothing here is a permission — the surface that composed this table decided
   * it would offer the column, and the write boundary still decides whether the write lands.
   *
   * A cell that supplies its own `{ node }` or `{ slot }` KEEPS IT. This flag is the declarative
   * form of what a host could already compose by hand, and the two coexist: the composed form is
   * how a host offers something this flag cannot express (a picklist, a lookup, a masked value), so
   * a declared column with a hand-built cell renders the hand-built one. A `total` row and a
   * `disabled` row are never editable.
   */
  editable?: boolean;
}

/**
 * A cell's content: a primitive (rendered as text), OR a descriptor that projects a
 * light-DOM leaf through a named `<slot>` (`{ slot }`), OR a caller-built DOM `node`
 * (e.g. `document.createElement('caos-status-pill')`). The slot/node forms compose the
 * AF1–AF3 leaves in a cell without any `innerHTML` (CSP-safe).
 */
export type CaosCellValue =
  | string
  | number
  | null
  | undefined
  | { text?: string; slot?: string; node?: Node };

export interface CaosTableRow {
  /** Stable id for selection events (falls back to the row index). */
  id?: string;
  /** Cell content keyed by column `key`. */
  cells: Record<string, CaosCellValue>;
  /** Reflected as `aria-selected` and the checkbox state. */
  selected?: boolean;
  /** A non-selectable / dimmed row (its checkbox is disabled). */
  disabled?: boolean;
  /** A summary / total row: an accent top rule + a bold display figure. Not selectable
   *  (no checkbox rendered for it). */
  total?: boolean;
  /**
   * CAOS-591 — the rows that sit UNDER this one. Present (even empty) ⇒ this row is a parent: it
   * draws a disclosure control in its first data cell, and its children follow it as ordinary rows
   * carrying their depth. They are rows in every other respect — same cells, same checkbox, same
   * actions cell — because a nested row that behaved differently would be a second kind of row
   * wearing the first one's clothes.
   */
  children?: CaosTableRow[];
}

export type CaosSortDirection = 'asc' | 'desc';

const sheet = makeSheet(`
  :host { display: block; }

  /* wide tables scroll IN PLACE — the page body never scrolls horizontally.
     Dragon CRM list card: strong border + card elevation. */
  .scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--caos-color-border-strong);
    border-radius: var(--caos-radius-lg);
    background: var(--caos-color-surface);
    box-shadow: var(--caos-shadow-card);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
    color: var(--caos-color-text);
  }

  /* Header: a ghosted mono micro-label on a transparent ground
     (labels arrive already cased — no forced uppercase; no sticky surface tint). */
  thead th {
    text-align: left;
    font-family: var(--caos-font-mono);
    font-weight: var(--caos-font-weight-label);
    font-size: 10px;
    letter-spacing: 0.14em;
    color: var(--caos-color-text-ghost);
    background: transparent;
    border-bottom: 1px solid var(--caos-color-border);
    padding: 0;
    white-space: nowrap;
  }
  th .cellpad, td .cellpad {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 11px 12px;
    min-width: 0;
  }
  th[data-align="end"] .cellpad, td[data-align="end"] .cellpad { justify-content: flex-end; }
  th[data-align="center"] .cellpad, td[data-align="center"] .cellpad { justify-content: center; }
  /* numeric columns read as a mono tabular ledger. */
  td[data-align="end"] { font-family: var(--caos-font-mono); font-variant-numeric: tabular-nums; }

  /* the sortable header is a real button — inherits the ghosted mono header face */
  .sort {
    appearance: none;
    border: none;
    background: transparent;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-1);
    padding: 0;
  }
  .sort:hover { color: var(--caos-color-text); }
  .sort:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }
  .sort .arrow { width: 14px; height: 14px; opacity: 0; transition: opacity 0.12s ease; }
  .sort .arrow svg { display: block; width: 100%; height: 100%; }
  th[aria-sort="ascending"] .sort, th[aria-sort="descending"] .sort { color: var(--caos-color-accent-strong); }
  th[aria-sort="ascending"] .sort .arrow, th[aria-sort="descending"] .sort .arrow { opacity: 1; }
  th[aria-sort="descending"] .sort .arrow { transform: rotate(180deg); }

  tbody td {
    border-bottom: 1px solid var(--caos-color-border);
    padding: 0;
    font-size: 13px;
    vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: none; }

  /* Total row — an accent rule above a bold display figure. */
  tbody tr[data-total="true"] td {
    border-top: 1px solid var(--caos-color-accent);
    font-family: var(--caos-font-display);
    font-size: 18px;
    font-weight: 700;
  }
  tbody tr { transition: background 0.1s ease; }
  tbody tr:hover { background: var(--caos-color-surface-2); }
  /* Selected row: mix the accent INTO the theme surface (not the theme-fixed pale
     accent-soft), so the row's normal --caos-color-text stays legible in BOTH themes —
     the AF2/AF3 dark-mode contrast lesson (a fixed pale tint under light text fails dark). */
  tbody tr[aria-selected="true"] { background: var(--caos-state-selected-bg); }
  tbody tr[aria-selected="true"]:hover { background: color-mix(in srgb, var(--caos-color-accent) 18%, var(--caos-color-surface)); }
  tbody tr[data-disabled="true"] { opacity: var(--caos-state-disabled-opacity); }
  .em-dash { color: var(--caos-color-text-muted); }

  /* Record-name + resolved-lookup hyperlinks in cells (Dragon list link). The link
     nodes are BUILT BY THE APP LAYER and appended into this shadow tree, so app CSS
     can't reach them — they are styled here. All cell links are accent red, NO underline
     at rest, underline on hover with a 2px offset. Only the record-NAME link is bold
     (the Dragon strong() name-link treatment); secondary lookup links stay normal weight. */
  td a, td a.caos-table__link {
    color: var(--caos-color-accent);
    font-weight: 400;
    text-decoration: none;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  td a.caos-table__link--name { font-weight: 700; }

  /* CAOS-499 — the same situation as the link rule above, for a cell whose destination is not a
     URL. A record id in a query result opens a record, but the component holding the result cannot
     know where records live, so it appends a real <button> and raises an event instead of building
     an href. Being a button rather than a link is what makes it keyboard-operable and announced as
     an action; it is drawn like the links beside it because to a reader it is the same affordance. */
  td button.caos-table__idlink {
    border: 0;
    background: none;
    padding: 0;
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-accent);
    text-decoration: none;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  td button.caos-table__idlink:hover { text-decoration: underline; }
  td button.caos-table__idlink:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* CAOS-499 — an editable cell is a GRID CELL, not a form field. Editing happens in the grid and
     there is no separate edit form, so the control has to read as part of the row at rest and as a
     control when it is being used: no box until hover or focus, the row's own type scale, and the
     cell's alignment. A control that drew a permanent input in every cell would turn a table
     somebody is reading into a form they are filling in.

     Like the link and id rules above, these are here because the nodes are BUILT BY THE COMPONENT
     COMPOSING THE TABLE and appended into this shadow tree, where no other sheet reaches them. */
  td input.caos-table__cell-edit, td select.caos-table__cell-edit {
    width: 100%;
    border: 1px solid transparent;
    border-radius: var(--caos-radius-sm);
    background: none;
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 13px;
    padding: 2px 5px;
  }
  td input.caos-table__cell-edit:hover, td select.caos-table__cell-edit:hover { border-color: var(--caos-color-border); }
  td input.caos-table__cell-edit:focus-visible, td select.caos-table__cell-edit:focus-visible {
    outline: none;
    border-color: var(--caos-color-accent);
    background: var(--caos-color-surface);
    box-shadow: var(--caos-focus-ring);
  }
  /* An edit that has not been saved yet. It uses the override tokens deliberately: those are the
     platform's existing "a human pinned this value" treatment, and an unsaved edit is exactly that
     until it lands. */
  td input.caos-table__cell-edit[data-changed="yes"] {
    background: var(--caos-color-override-bg);
    border-color: var(--caos-color-override-border);
    color: var(--caos-color-override-text);
  }

  /* Why a cell has no control. Not a greyed-out box: that says only that you cannot, which is the
     half the reader already worked out from it not responding. */
  td .caos-table__cell-note { color: var(--caos-color-text-muted); font-style: italic; }

  /* CAOS-499 — detail opened INSIDE a row rather than in a modal. A failure whose message and trace
     live behind a modal is a failure you read one at a time, and reading six failures usually means
     noticing what three of them have in common. Same seam as the rules above: built by the
     composing component, appended here, reachable by no other sheet. */
  td .caos-table__detail-message { margin: 0; color: var(--caos-color-text); font-size: 12.5px; }
  td .caos-table__detail-toggle {
    border: 0;
    background: none;
    padding: 2px 0 0;
    color: var(--caos-color-accent);
    font-family: var(--caos-font);
    font-size: 12px;
    cursor: pointer;
  }
  td .caos-table__detail-toggle:hover { text-decoration: underline; }
  td .caos-table__detail-toggle:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  td .caos-table__detail-stack {
    margin: 4px 0 0;
    padding: 6px 8px;
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 220px;
    overflow: auto;
  }
  td a:hover, td a.caos-table__link:hover { text-decoration: underline; }
  td a:focus-visible {
    outline: none;
    box-shadow: var(--caos-focus-ring);
    border-radius: var(--caos-radius-sm);
  }

  /* the checkbox column is snug */
  .selcol { width: 1%; }
  .selcol .cellpad { padding-right: var(--caos-space-2); }

  /* CAOS-1671 — the row-number gutter. Snug, right-aligned and muted, because a position is not
     data: at full strength and left-aligned it reads as the record's first field. Tabular figures
     keep the digits in a column of their own as the numbers gain places, so row 9 and row 10 do
     not shuffle the rule between the gutter and the first real cell. */
  .numcol { width: 1%; }
  .numcol .cellpad {
    padding-right: var(--caos-space-2);
    text-align: right;
    color: var(--caos-color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  /* CAOS-591 — a nested row is an ORDINARY row, indented by its depth, with the disclosure control
     in its first data cell. No second table, no second kind of row: the grid stays a grid, so
     selection, row actions, sorting and the two-dimensional keyboard reach a nested row unchanged. */
  .twisty {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    font: inherit;
    line-height: 1;
    padding: 0;
    width: 16px;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.12s ease;
  }
  .twisty:hover { color: var(--caos-color-text); }
  .twisty:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }
  tbody tr[aria-expanded="false"] .twisty { transform: rotate(-90deg); }
  /* A row with nothing under it keeps the control's width, so every label in the column lines up. */
  .twisty-gap { width: 16px; flex: 0 0 auto; }
  /* The indent sits on the first DATA cell, so the checkbox column stays flush down the table. */
  td.firstcol .cellpad { padding-left: calc(12px + var(--row-depth, 0) * var(--caos-space-5)); }

  .empty td { padding: 0; border-bottom: none; }

  .footer { display: block; }
  .footer:not(:has(*)) { display: none; }
  .footer ::slotted([slot="footer"]) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--caos-space-3);
    padding: var(--caos-space-3) var(--caos-space-4);
    border-top: 1px solid var(--caos-color-border);
  }
`);
let uid = 0;

/**
 * CAOS-1677 — the key an editable cell is remembered under, for {@link CaosDataTable.settleCell}.
 *
 * A NUL joiner rather than a colon or a dash: a row id and a column key are both caller text, and
 * any separator either of them could contain would let two different cells share a key. A row id is
 * `String(row.id)`, so it can hold anything a host puts in an id.
 */
function cellSettleKey(rowId: string, columnKey: string): string {
  return `${rowId}\u0000${columnKey}`;
}

export class CaosDataTable extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'loading',
      'loading-rows',
      'selectable',
      'empty-label',
      'label',
      'expandable',
      'row-numbers',
      'row-number-start',
    ];
  }

  private scrollBox: HTMLDivElement;
  private table: HTMLTableElement;
  private thead: HTMLTableSectionElement;
  private tbody: HTMLTableSectionElement;
  private tfoot: HTMLTableSectionElement;
  private headerRow: HTMLTableRowElement;
  private selectAll: CaosCheckboxField | null = null;

  private _columns: CaosTableColumn[] = [];
  private _rows: CaosTableRow[] = [];
  /**
   * CAOS-1677 — one entry per editable cell currently in the DOM, keyed row-then-column: how
   * {@link settleCell} rebases that cell's baseline once a host's save has landed.
   *
   * Held here rather than derived from the DOM because the baseline is a closure variable, not an
   * attribute. Cleared on every body render; see {@link renderBody}.
   */
  private cellSettle = new Map<string, (outcome: 'saved' | 'refused', value: string) => void>();
  private _sort: { key: string; direction: CaosSortDirection } | null = null;
  /**
   * CAOS-591 — the parent rows a reader has CLOSED. Held as the closed set rather than the open one
   * so a parent arriving in new data is open: the default this table promises, rather than hidden
   * because nobody had opened something that did not exist yet.
   */
  private _collapsed = new Set<string>();
  private readonly seq = ++uid;

  // ── CAOS-601, virtualisation ──────────────────────────────────────────────────────────────────
  // Behaviour, not a variant. Nobody places a different table because the object turned out to have
  // more rows than they expected; the table notices for itself and holds back what is not near the
  // viewport, leaving the space it would have taken so the scrollbar still describes the LIST.
  /** How tall one row is, measured off what has been painted. 0 until something has. */
  private rowHeight = 0;
  /** The slice of `visibleRows()` currently in the DOM. */
  private window: VirtualWindow = wholeList(0);
  private frame: number | null = null;

  private readonly onBodyScroll = (): void => {
    if (this.frame !== null) return;
    const view = this.ownerDocument.defaultView;
    if (!view) return;
    this.frame = view.requestAnimationFrame(() => {
      this.frame = null;
      const next = virtualWindow(this.bodyMetrics());
      if (next.start === this.window.start && next.end === this.window.end) return;
      this.renderBody();
    });
  };

  constructor() {
    super(sheet);

    this.scrollBox = document.createElement('div');
    this.scrollBox.className = 'scroll';
    this.scrollBox.setAttribute('part', 'scroll');

    this.table = document.createElement('table');
    this.table.setAttribute('part', 'table');
    this.table.setAttribute('role', 'grid');
    this.table.addEventListener('keydown', (e) => this.onKeydown(e));

    this.thead = document.createElement('thead');
    this.thead.setAttribute('part', 'head');
    this.headerRow = document.createElement('tr');
    this.headerRow.setAttribute('part', 'header-row');
    this.headerRow.setAttribute('role', 'row');
    this.thead.append(this.headerRow);

    this.tbody = document.createElement('tbody');
    this.tbody.setAttribute('part', 'body');

    this.tfoot = document.createElement('tfoot');

    this.table.append(this.thead, this.tbody, this.tfoot);
    this.scrollBox.append(this.table);

    const footer = document.createElement('div');
    footer.className = 'footer';
    footer.setAttribute('part', 'footer');
    const footerSlot = document.createElement('slot');
    footerSlot.name = 'footer';
    footer.append(footerSlot);

    this.root.append(this.scrollBox);
    // the footer lives OUTSIDE the scroll container so it stays put while the table scrolls
    this.root.append(footer);
  }

  connectedCallback(): void {
    this.syncName();
    this.scrollBox.addEventListener('scroll', this.onBodyScroll, { passive: true });
    this.render();
  }

  disconnectedCallback(): void {
    this.scrollBox.removeEventListener('scroll', this.onBodyScroll);
    if (this.frame !== null) {
      this.ownerDocument.defaultView?.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  /**
   * What the table can see about itself. Every number can be missing — before a first paint, in a
   * container that is not on screen — and `virtualWindow` answers "render everything" to all of
   * them, so an unmeasurable table is a slow table and never an empty one.
   */
  private bodyMetrics(): VirtualWindowInput {
    return {
      count: this.visibleRows().length,
      rowHeight: this.rowHeight,
      viewport: this.scrollBox.clientHeight,
      scrollTop: this.scrollBox.scrollTop,
      columns: 1,
      overscan: DEFAULT_OVERSCAN,
    };
  }

  /** Measure one row off what was painted. Rows are uniform here, so one of them is the answer. */
  private measureRowHeight(): void {
    const tr = this.tbody.querySelector<HTMLElement>('tr[data-row-index]');
    const height = tr?.offsetHeight ?? 0;
    if (height > 0) this.rowHeight = height;
  }

  /** A band of empty space standing in for rows that are not in the DOM. */
  private spacerRow(height: number, colCount: number): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.className = 'spacer';
    tr.setAttribute('aria-hidden', 'true');
    const td = document.createElement('td');
    td.colSpan = Math.max(1, colCount);
    td.style.height = `${height}px`;
    td.style.padding = '0';
    tr.append(td);
    return tr;
  }

  /**
   * Put `index` in the window if it is not already, so a key that moves onto a row can always find
   * one. Arrowing down the last painted row must step onto the next RECORD — stopping there would
   * be a rendering optimisation the reader can feel, which is the one thing it must never be.
   */
  private ensureRowRendered(index: number): boolean {
    if (!this.window.windowed) return false;
    if (index >= this.window.start && index < this.window.end) return false;
    this.window = windowContaining(this.window, index, this.bodyMetrics());
    this.renderBody(this.window);
    return true;
  }

  /**
   * CAOS-1672 — does the focused control need this key for itself, rather than for moving around the
   * grid?
   *
   * A CARET needs the horizontal keys: Left/Right move it by a character and Home/End go to the
   * ends of the text, which is also why the vertical keys are NOT claimed — a single-line text box
   * has nothing to do with Up/Down, so those keep moving between rows and the two-dimensional model
   * survives. A `select` is the other way round: Up/Down are how it changes option natively, and it
   * has no caret.
   *
   * Read off the element, not off a flag this component sets, so a control a host composed by hand
   * behaves the same as one this component drew. The type list is the text-entry set — `checkbox`,
   * `radio`, `button` and the rest have no caret and stay navigable.
   */
  private controlOwnsKey(active: Element | null, key: string): boolean {
    if (!active) return false;
    const horizontal = key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End';
    const tag = active.tagName;
    if (tag === 'SELECT') return key === 'ArrowUp' || key === 'ArrowDown';
    if (tag === 'TEXTAREA') return true;
    if ((active as HTMLElement).isContentEditable) return true;
    if (tag !== 'INPUT') return false;
    const type = ((active as HTMLInputElement).type || 'text').toLowerCase();
    const CARET_TYPES = ['text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date', 'time', 'datetime-local', 'month', 'week'];
    return horizontal && CARET_TYPES.includes(type);
  }

  /** The row index a focused element sits in, walking out through every shadow boundary on the way. */
  private rowIndexOf(el: Element | null): number | null {
    let node: Element | null = el;
    while (node) {
      if (node instanceof HTMLTableRowElement) {
        const raw = node.dataset.rowIndex;
        return raw === undefined ? null : Number(raw);
      }
      const root = node.getRootNode();
      node = node.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
    }
    return null;
  }

  attributeChangedCallback(name: string): void {
    // CAOS-648 — `label` is the grid's accessible name. It changes nothing in the rows, so it is kept
    // in step on its own rather than by a re-render that never looked at it.
    if (name === 'label') {
      this.syncName();
      return;
    }
    if (this.isConnected) this.render();
  }

  /** The grid is named by `label`; without one it has no name, like a table never given one. */
  private syncName(): void {
    applyAccessibleName(this.table, this.getAttribute('label'), null);
  }

  /** Columns (data in). */
  get columns(): CaosTableColumn[] {
    return this._columns;
  }
  set columns(list: CaosTableColumn[]) {
    this._columns = list ?? [];
    this.render();
  }

  /** Rows (data in — no live fetch; the access-filtered set is supplied by the app). */
  get rows(): CaosTableRow[] {
    return this._rows;
  }
  set rows(list: CaosTableRow[]) {
    this._rows = list ?? [];
    this.render();
  }

  /** The current sort, or null. Setting it reflects `aria-sort`; it does NOT reorder rows. */
  get sort(): { key: string; direction: CaosSortDirection } | null {
    return this._sort;
  }
  set sort(v: { key: string; direction: CaosSortDirection } | null) {
    this._sort = v;
    this.reflectSort();
  }

  /**
   * CAOS-1671 — the leading column of row numbers.
   *
   * ── WHAT THE NUMBER COUNTS, WHICH IS THE WHOLE DECISION ───────────────────────────────
   * A number against a row can mean two different things the moment a list is paged: the row's
   * position among the rows on screen, or its position in the whole result set. A reader cannot
   * tell which from the number alone, so it has to be settled rather than left to the caller to
   * guess.
   *
   * It is settled by what this component actually knows. `rows` is "data in — no live fetch; the
   * access-filtered set is supplied by the app", and `sort` "does NOT reorder rows": the table is
   * handed a list already in display order and has no idea whether it is all of them, nor what
   * page it is, nor how many there are in total. So the number it can compute unaided is a
   * position among the rows SHOWN, and nothing else is available to it.
   *
   * Shown rather than held, and the difference is visible in one case: closing a parent takes its
   * children out of the list, and the rows below it move up a number. That follows from the choice
   * rather than contradicting it, and it is worth knowing before switching this on beside
   * `expandable`.
   *
   * SHOWN MEANS THE VISIBLE LIST, NOT WHAT IS IN VIEW. Virtualisation renders a window of that list
   * and nothing else, but the numbering is computed over the whole of it, so scrolling never
   * changes a row's number. Only collapsing does, because only collapsing takes a row out of the
   * list rather than out of the viewport.
   *
   * A result-set ordinal is therefore expressible but not derivable: the app says where its page
   * begins, through {@link rowNumberStart}, and the numbers continue from there. Unset, the first
   * row the table holds is 1, which is the right answer for every list that is not paged — the
   * overwhelming majority — and asks nothing of the caller to get it.
   */
  get rowNumbers(): boolean {
    return this.hasAttribute('row-numbers');
  }
  set rowNumbers(v: boolean) {
    if (v) this.setAttribute('row-numbers', '');
    else this.removeAttribute('row-numbers');
  }

  /**
   * The number given to the FIRST row this table holds. Default 1.
   *
   * A paged list sets this to the offset of its page — the third page at 25 rows a page sets 51 —
   * and the gutter then reads as a position in the result set instead of in the page. Both numbers
   * are named because "page 3 of 25" does not say it: it reads as the third of twenty-five pages,
   * which fixes no offset at all.
   *
   * Every operand is checked, because this arrives as a string from an attribute. Anything `Number`
   * cannot read as a finite value of 1 or more falls back to 1 — absent, empty, `5px`, `Infinity`,
   * zero and negatives alike — since a position people read is 1-based and a gutter starting at 0
   * or -4 would be a typo rendered faithfully rather than an intention. `Number` is lenient in two
   * ways worth knowing rather than defending: `1e3` reads as 1000 and `0x10` as 16. Both are
   * finite and at least 1, so both are honoured. That is a consequence of using the platform's own
   * parser rather than a decision, and it costs nothing to leave: a caller writing either one in
   * this attribute has said a number, however oddly.
   */
  get rowNumberStart(): number {
    const raw = Number(this.getAttribute('row-number-start'));
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
  }
  set rowNumberStart(v: number) {
    this.setAttribute('row-number-start', String(v));
  }

  get selectable(): boolean {
    return this.hasAttribute('selectable');
  }
  set selectable(v: boolean) {
    if (v) this.setAttribute('selectable', '');
    else this.removeAttribute('selectable');
  }

  /** Whether a reader may open and close the rows under a parent row. */
  get expandable(): boolean {
    return this.hasAttribute('expandable');
  }
  set expandable(v: boolean) {
    if (v) this.setAttribute('expandable', '');
    else this.removeAttribute('expandable');
  }

  /**
   * CAOS-591 — EVERY row, parents and children alike, in reading order, each with the id it answers
   * to and how deep it sits.
   *
   * Every path that used to walk `_rows` walks this instead. A nested row invisible to the selection
   * paths would be a row a reader can tick and no caller can act on — the checkbox would work and
   * the answer would be wrong, which is worse than nesting simply not being offered.
   *
   * An id-less row still falls back to its index, so a flat table's ids are exactly what they always
   * were; nesting appends a segment beneath that rather than renumbering anything above it.
   */
  private allRows(): Array<{ row: CaosTableRow; id: string; depth: number }> {
    const out: Array<{ row: CaosTableRow; id: string; depth: number }> = [];
    const walk = (rows: readonly CaosTableRow[], depth: number, prefix: string): void => {
      rows.forEach((row, i) => {
        const path = prefix === '' ? String(i) : `${prefix}.${i}`;
        out.push({ row, id: row.id ?? path, depth });
        if (row.children) walk(row.children, depth + 1, path);
      });
    };
    walk(this._rows, 0, '');
    return out;
  }

  /** The rows on screen: a closed parent contributes itself and not what is under it. */
  private visibleRows(): Array<{ row: CaosTableRow; id: string; depth: number }> {
    const out: Array<{ row: CaosTableRow; id: string; depth: number }> = [];
    const walk = (rows: readonly CaosTableRow[], depth: number, prefix: string): void => {
      rows.forEach((row, i) => {
        const path = prefix === '' ? String(i) : `${prefix}.${i}`;
        const id = row.id ?? path;
        out.push({ row, id, depth });
        if (row.children && this.isOpen(id)) walk(row.children, depth + 1, path);
      });
    };
    walk(this._rows, 0, '');
    return out;
  }

  /**
   * CAOS-1671 — the position shown against each visible row, or null for a row that has none.
   *
   * ONE SOURCE, TWO READERS. The printed gutter and the row checkbox's accessible name both come
   * from this, so the number a reader sees and the number a screen reader says cannot disagree.
   * Before this existed the checkbox named its row by its index in the rendered list, which is the
   * same answer only while no `total` row sits above it and no start offset is declared.
   *
   * A `total` row gets no number and consumes no position: a summary line is not the nth record,
   * and numbering it would push every record below it one out of step with the result set. A
   * NESTED row does get one, continuing the sequence, because `expandable` already declares that a
   * nested row is an ordinary row — giving children a separate numbering, or none, would make the
   * gutter mean two things in one table.
   *
   * Computed over the WHOLE visible list rather than the rendered window, so virtualisation cannot
   * renumber a row by scrolling it into view.
   */
  private rowPositions(visible: ReadonlyArray<{ row: CaosTableRow }>): Array<number | null> {
    const start = this.rowNumberStart;
    let seen = 0;
    return visible.map(({ row }) => (row.total ? null : start + seen++));
  }

  /** A parent is open unless an expandable table has been told to close it. */
  private isOpen(id: string): boolean {
    return !this.expandable || !this._collapsed.has(id);
  }

  /** The ids (or index strings) of the currently selected rows — a nested row among them. */
  get selectedIds(): string[] {
    return this.allRows()
      .filter(({ row }) => row.selected)
      .map(({ id }) => id);
  }

  /**
   * CAOS-1677 — what a host calls once a save for this cell has actually landed.
   *
   * The unsaved marker is not a flag this component sets and clears; it is the difference between
   * what the row declared and what the control holds, which is what lets the table draw it while
   * owning no state. The stylesheet states what it claims — "an edit that has not been saved yet
   * ... an unsaved edit is exactly that until it lands" — so after a successful save the marker is
   * a false statement until something moves the baseline. This moves it, to the value that cell
   * last committed.
   *
   * It writes nothing into the control and touches neither focus nor caret, which is the whole
   * reason it exists rather than the host re-rendering `rows`. The editor is a persistent input
   * that keeps focus through a commit, so a save resolving while somebody types the next value must
   * not put a value in front of them; and re-rendering the rows would discard every other
   * in-progress edit in the grid to settle one cell.
   *
   * Returns false when there is no such editable cell in the DOM — a row scrolled out of a virtual
   * window, a column no longer drawn, or a save that resolved after THIS TABLE was replaced. A host
   * that rebuilds its body per repaint drops the element these closures belong to, and a dropped
   * element never renders again and so never clears its own registry: without the connected check
   * it would answer `true` from off-screen, and the host would take that for "the screen is showing
   * the saved value" when the screen is a different table still showing the old one.
   *
   * A host SHOULD check it. The false is how it learns that the answer reached nothing and that it
   * must repaint from the record it just adopted.
   *
   * `saved` is the value the host actually wrote — the one it was handed by the commit, not the
   * Engine's canonical form of it. That is what makes two saves in flight safe: an answer about an
   * older value cannot clear the marker for a newer edit. It is a BASELINE and is never written
   * into the control, so the caret is untouched either way.
   */
  settleCell(rowId: string, columnKey: string, saved: string): boolean {
    if (!this.isConnected) return false;
    const answer = this.cellSettle.get(cellSettleKey(rowId, columnKey));
    if (!answer) return false;
    answer('saved', saved);
    return true;
  }

  /**
   * CAOS-1677 — what a host calls when a save for this cell did NOT land.
   *
   * The pair of {@link settleCell}, and not optional: without it the same value can never be
   * committed again. Committing records what was reported so that the blur after Enter does not
   * report it twice, which means that after a refusal the cell is still holding the refused value
   * and a second Enter emits nothing — the person has to change the text and change it back. This
   * forgets the report, so pressing Enter again tries again. That is what a host which re-read the
   * record on a conflict is waiting for.
   *
   * It changes nothing a person can see: the typed value stays, and so does the unsaved marker,
   * which at that point is a true statement about a cell holding an edit the record does not have.
   *
   * `refused` is the value the save was about, so an answer to an older save cannot discard a newer
   * edit that is still awaiting its own.
   */
  refuseCell(rowId: string, columnKey: string, refused: string): boolean {
    if (!this.isConnected) return false;
    const answer = this.cellSettle.get(cellSettleKey(rowId, columnKey));
    if (!answer) return false;
    answer('refused', refused);
    return true;
  }

  /** Which parent rows are open, as ids. Setting it closes every parent not named. */
  get expandedIds(): string[] {
    return this.allRows()
      .filter(({ row }) => row.children)
      .map(({ id }) => id)
      .filter((id) => !this._collapsed.has(id));
  }
  set expandedIds(list: readonly string[]) {
    const open = new Set(Array.isArray(list) ? list : []);
    this._collapsed = new Set(
      this.allRows()
        .filter(({ row }) => row.children)
        .map(({ id }) => id)
        .filter((id) => !open.has(id)),
    );
    this.renderBody();
  }

  private render(): void {
    this.renderHeader();
    this.renderBody();
  }

  private renderHeader(): void {
    this.headerRow.replaceChildren();
    this.selectAll = null;

    if (this.selectable) {
      const th = document.createElement('th');
      th.className = 'selcol';
      th.setAttribute('part', 'header-cell');
      th.setAttribute('role', 'columnheader');
      th.scope = 'col';
      const pad = document.createElement('div');
      pad.className = 'cellpad';
      const cb = document.createElement('caos-checkbox-field') as CaosCheckboxField;
      cb.setAttribute('part', 'select-all');
      cb.setAttribute('label', '');
      cb.setAttribute('aria-label', 'Select all rows');
      cb.addEventListener('change', () => this.onSelectAll(cb));
      pad.append(cb);
      th.append(pad);
      this.headerRow.append(th);
      this.selectAll = cb;
    }

    // CAOS-1671 — after the checkbox and before the data. The gutter identifies a row rather than
    // acting on it, so it belongs inside the one control that sits in the row's margin rather than
    // outside it. Whatever the reason, the order has to be the SAME in all three builders here, and
    // there is a test for that: the first draft of this change put the skeleton's cell on the other
    // side of the checkbox, which misaligns the columns only while the table is loading.
    if (this.rowNumbers) {
      const th = document.createElement('th');
      th.className = 'numcol';
      th.setAttribute('part', 'header-cell');
      th.setAttribute('role', 'columnheader');
      th.scope = 'col';
      // Blank on purpose: a visible word over a column of positions reads as another field of the
      // record. It still has to be NAMED, though — an unnamed column header is announced as
      // nothing at all — so the name is carried for a screen reader and drawn for nobody.
      th.setAttribute('aria-label', 'Row number');
      const pad = document.createElement('div');
      pad.className = 'cellpad';
      th.append(pad);
      this.headerRow.append(th);
    }

    this._columns.forEach((col) => {
      const th = document.createElement('th');
      th.setAttribute('part', 'header-cell');
      th.setAttribute('role', 'columnheader');
      th.scope = 'col';
      if (col.align) th.dataset.align = col.align;
      if (col.width) th.style.width = col.width;
      const pad = document.createElement('div');
      pad.className = 'cellpad';

      if (col.sortable) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sort';
        btn.setAttribute('part', 'sort-button');
        btn.dataset.key = col.key;
        btn.append(document.createTextNode(col.label));
        const arrow = document.createElement('span');
        arrow.className = 'arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.append(this.sortArrow());
        btn.append(arrow);
        btn.addEventListener('click', () => this.onSortToggle(col.key));
        pad.append(btn);
        th.setAttribute('aria-sort', 'none');
      } else {
        pad.append(document.createTextNode(col.label));
      }
      th.append(pad);
      this.headerRow.append(th);
    });

    this.reflectSort();
    this.syncSelectAll();
  }

  private renderBody(forced?: VirtualWindow): void {
    this.tbody.replaceChildren();
    // CAOS-1677 — the editors just discarded cannot be settled any more. Cleared HERE rather than
    // when each one is built, because virtual scrolling rebuilds the body with a different window
    // and a stale entry would hold a closure over a detached input: `settleCell` would report true
    // having cleared a marker nobody can see.
    this.cellSettle.clear();
    const colCount = this._columns.length + (this.selectable ? 1 : 0) + (this.rowNumbers ? 1 : 0);

    if (this.hasAttribute('loading')) {
      const n = Number(this.getAttribute('loading-rows') ?? '3') || 3;
      for (let r = 0; r < n; r++) this.tbody.append(this.skeletonRow());
      return;
    }

    if (this._rows.length === 0) {
      const tr = document.createElement('tr');
      tr.className = 'empty';
      const td = document.createElement('td');
      td.colSpan = Math.max(1, colCount);
      const empty = document.createElement('caos-empty-state');
      empty.setAttribute('title', this.getAttribute('empty-label') ?? 'No records');
      empty.append(document.createTextNode('Nothing to show yet. Records will appear here once they exist.'));
      // let the host supply its own empty content via the `empty` slot, if any
      const slot = document.createElement('slot');
      slot.name = 'empty';
      slot.append(empty);
      td.append(slot);
      tr.append(td);
      this.tbody.append(tr);
      return;
    }

    // A grid whose rows nest IS a treegrid. A table of flat rows keeps the role it always had, so
    // this says something new only where something new is true.
    const nested = this.allRows().some(({ row }) => row.children);
    this.table.setAttribute('role', nested ? 'treegrid' : 'grid');

    // CAOS-601 — which rows are worth having in the DOM. `wholeList` for anything short enough
    // not to be worth the trouble, which is the overwhelming majority of tables and is byte for byte
    // what this did before.
    const visible = this.visibleRows();
    // CAOS-1671 — over the whole visible list, before the window narrows it.
    const positions = this.rowPositions(visible);
    this.window = forced ?? virtualWindow({ ...this.bodyMetrics(), count: visible.length });
    const win = this.window;
    if (win.windowed && win.leading > 0) this.tbody.append(this.spacerRow(win.leading, colCount));

    visible.slice(win.start, win.end).forEach(({ row, id, depth }, offset) => {
      const index = win.start + offset;
      const hasKids = Array.isArray(row.children);
      const open = this.isOpen(id);
      const tr = document.createElement('tr');
      tr.setAttribute('part', 'row');
      tr.setAttribute('role', 'row');
      tr.dataset.rowId = id;
      if (nested) tr.setAttribute('aria-level', String(depth + 1));
      // Only an expandable table says whether a row is open; one that cannot be closed claims nothing.
      if (hasKids && this.expandable) tr.setAttribute('aria-expanded', String(open));
      if (row.disabled) tr.dataset.disabled = 'true';
      if (row.total) tr.dataset.total = 'true';
      // a total/summary row is not selectable — no aria-selected, no checkbox
      if (this.selectable && !row.total) tr.setAttribute('aria-selected', String(!!row.selected));

      const position = positions[index];

      if (this.selectable) {
        const td = document.createElement('td');
        td.className = 'selcol';
        td.setAttribute('part', 'cell');
        td.setAttribute('role', 'gridcell');
        const pad = document.createElement('div');
        pad.className = 'cellpad';
        // total rows keep the column alignment but carry no checkbox
        if (!row.total) {
          const cb = document.createElement('caos-checkbox-field') as CaosCheckboxField;
          cb.setAttribute('part', 'row-select');
          cb.setAttribute('label', '');
          // The same number the gutter prints, whether or not the gutter is shown. `position` is
          // null only for a `total` row, and a total row carries no checkbox, so the fallback is
          // unreachable and exists to keep the expression total rather than to be relied on.
          cb.setAttribute('aria-label', `Select row ${position ?? index + 1}`);
          if (row.selected) cb.setAttribute('checked', '');
          if (row.disabled) cb.setAttribute('disabled', '');
          cb.addEventListener('change', () => this.onRowSelect(id, cb));
          pad.append(cb);
        }
        td.append(pad);
        tr.append(td);
      }

      if (this.rowNumbers) {
        const td = document.createElement('td');
        td.className = 'numcol';
        td.setAttribute('part', 'cell');
        td.setAttribute('role', 'gridcell');
        const pad = document.createElement('div');
        pad.className = 'cellpad';
        // A total row keeps the cell, so the columns stay in line, and keeps it empty.
        if (position !== null) pad.append(document.createTextNode(String(position)));
        td.append(pad);
        tr.append(td);
      }

      this._columns.forEach((col, ci) => {
        const td = document.createElement('td');
        td.setAttribute('part', 'cell');
        td.setAttribute('role', 'gridcell');
        if (col.align) td.dataset.align = col.align;
        const pad = document.createElement('div');
        pad.className = 'cellpad';
        // The disclosure and the indent live in the FIRST DATA cell, so the checkbox column stays
        // flush and the row keeps exactly the cells its columns describe.
        if (ci === 0 && nested) {
          td.classList.add('firstcol');
          td.style.setProperty('--row-depth', String(depth));
          if (hasKids && this.expandable) pad.append(this.rowTwisty(row, id, open));
          else if (this.expandable) {
            const gap = document.createElement('span');
            gap.className = 'twisty-gap';
            gap.setAttribute('aria-hidden', 'true');
            pad.append(gap);
          }
        }
        // CAOS-1672 — a total row is a summary and a disabled row is not actionable, so neither
        // offers an editor however the column is declared. Same rule the checkbox column already
        // applies to those two rows.
        const editable = col.editable === true && row.total !== true && row.disabled !== true;
        this.fillCell(pad, row.cells[col.key], row, id, col.key, editable ? col : undefined);
        td.append(pad);
        tr.append(td);
      });

      tr.dataset.rowIndex = String(index);
      this.tbody.append(tr);
    });

    if (win.windowed && win.trailing > 0) this.tbody.append(this.spacerRow(win.trailing, colCount));

    // The first paint has nothing to measure against, so it measures itself and asks again. Only
    // once: a second unmeasurable answer means the table is not on screen, and asking forever
    // would be a loop.
    if (this.rowHeight === 0) {
      this.measureRowHeight();
      if (this.rowHeight > 0 && forced === undefined) {
        const next = virtualWindow({ ...this.bodyMetrics(), count: visible.length });
        if (next.start !== win.start || next.end !== win.end) {
          this.renderBody(next);
          return;
        }
      }
    }

    this.syncSelectAll();
  }

  /**
   * Render one cell's content: primitive → text; `{slot}` → named slot; `{node}` → append.
   *
   * CAOS-1672 — `editableColumn` present ⇒ this cell is offered for editing and draws a control
   * instead of text. A cell that brought its OWN `{node}` or `{slot}` is left alone: the composed
   * form is how a host expresses an editor this flag cannot, and overwriting it would make the
   * declarative flag destructive rather than additive.
   */
  private fillCell(
    pad: HTMLElement,
    value: CaosCellValue,
    row: CaosTableRow,
    rowId: string,
    key: string,
    editableColumn?: CaosTableColumn,
  ): void {
    const composed = value != null && typeof value === 'object' && (value.node != null || value.slot != null);
    if (editableColumn && !composed) {
      pad.append(this.cellEditor(value, row, rowId, editableColumn));
      return;
    }
    if (value == null) {
      pad.append(this.emDash());
      return;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const text = String(value);
      pad.append(text.length ? document.createTextNode(text) : this.emDash());
      return;
    }
    // descriptor
    if (value.slot) {
      const slot = document.createElement('slot');
      slot.name = value.slot;
      pad.append(slot);
      return;
    }
    if (value.node) {
      pad.append(value.node);
      return;
    }
    const text = value.text ?? '';
    pad.append(text.length ? document.createTextNode(text) : this.emDash());
    // `rowId` is read by the editable path above. `key` is still the seam it always was — the
    // editable path reads the column's own key, so nothing here needs it yet.
    void key;
  }

  /**
   * CAOS-1672 — the control for one editable cell, over the cell-edit presentation this component
   * ships (`.caos-table__cell-edit`): no box until hover or focus, the row's own type scale, the
   * cell's alignment, and the override treatment once the value differs from the one the row
   * declared.
   *
   * The declared value is the baseline for "changed", which is what lets the table draw the unsaved
   * marker without owning any state: the row says what the value is, the control says what it is
   * now, and the difference is the edit. That also makes Escape expressible — it puts the declared
   * value back — so a reader who starts typing in a grid is not trapped in a cell.
   *
   * It emits, and writes nothing:
   *   `caos-cell-edit`   — on each change of the control, for a host that wants to validate or
   *                        count unsaved edits as they happen.
   *   `caos-cell-commit` — when the edit is COMMITTED (Enter, or leaving the cell) and the value
   *                        actually differs. This is the one that means "save this".
   * A host that re-renders `rows` on `caos-cell-edit` destroys the control the person is typing in;
   * the commit event is the one to write from.
   */
  private cellEditor(value: CaosCellValue, row: CaosTableRow, rowId: string, column: CaosTableColumn): HTMLInputElement {
    /*
     * CAOS-1677 — `let`, not `const`: this is "the value the HOST holds", and a save that lands
     * moves it. It is the baseline for the unsaved marker, for `previous`, and for what Escape puts
     * back, and all three stay correct only if settling rebases it rather than clearing a flag.
     */
    let declared =
      value == null ? '' : typeof value === 'object' ? (value.text ?? '') : String(value);
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'caos-table__cell-edit';
    input.setAttribute('part', 'cell-edit');
    input.value = declared;
    // The column heading alone would announce every control in the column identically. The row's
    // name cell is what distinguishes them, so the name is the label's subject when there is one.
    const subject = this.rowLabel(row);
    input.setAttribute('aria-label', subject === '' ? column.label : `${column.label}, ${subject}`);

    // The last value this cell actually COMMITTED, which starts as the one the row declared. It is
    // not the same as `declared` and cannot be folded into it: `declared` is what the host still
    // holds, so it stays the baseline for `previous` and for the unsaved marker, while this is what
    // has already been reported. Without it, Enter commits and the blur that follows — the control
    // keeps focus, so leaving the cell blurs it — reports the SAME edit a second time, and a host
    // that writes on commit sends the same write twice. Because each row carries its own version,
    // the second write can then be refused as a conflict with the host's own first save.
    let committed = declared;
    /*
     * The value this cell last had REFUSED, so that leaving the cell does not re-send it. A refusal
     * puts `committed` back to the baseline on purpose — that is what lets Enter try the same value
     * again — but the box still holds the refused text, so the blur on the way out would see a value
     * that differs from `committed` and commit it a second time: two toasts for one mistake, and for
     * a conflict two write attempts from one Tab. Enter asks again; walking away does not.
     */
    let refused: string | null = null;

    const mark = (): void => {
      if (input.value === declared) input.removeAttribute('data-changed');
      else input.setAttribute('data-changed', 'yes');
    };
    const emit = (name: 'caos-cell-edit' | 'caos-cell-commit'): void => {
      if (name === 'caos-cell-commit') committed = input.value;
      this.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          composed: true,
          detail: { id: rowId, column: column.key, value: input.value, previous: declared },
        }),
      );
    };
    /**
     * Commit, unless this exact value has already been committed from this cell. `explicit` is the
     * difference between asking (Enter) and leaving (blur): only asking retries a refusal.
     */
    const commit = (explicit: boolean): void => {
      if (!explicit && input.value === refused) return;
      if (input.value !== committed) emit('caos-cell-commit');
    };

    input.addEventListener('input', () => {
      mark();
      emit('caos-cell-edit');
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit(true);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        input.value = declared;
        mark();
        emit('caos-cell-edit');
      }
    });
    // Leaving the cell commits, which is what a grid does — an edit abandoned by clicking the next
    // cell is still an edit somebody made. Escape is how it is taken back. An edit already
    // committed on Enter is not reported again here; one that moved on since is.
    input.addEventListener('blur', () => commit(false));
    /*
     * CAOS-1677 — how a host's answer reaches this cell. A closure rather than a DOM query, so what
     * it moves is the same binding the marker, `previous` and Escape all read.
     *
     * It takes the value the answer is ABOUT, which is what makes two saves in flight safe. The
     * host answers each commit with the value it committed, so an answer to an older save cannot
     * claim a newer edit: settling `a` while the box holds `b` sets the baseline to `a` and leaves
     * the cell correctly marked, instead of clearing a marker for a value nobody has saved.
     */
    this.cellSettle.set(cellSettleKey(rowId, column.key), (outcome, value) => {
      if (outcome === 'saved') {
        declared = value;
        refused = null;
        /*
         * And in the row data too, or this table's OWN next repaint undoes it. `declared` is a
         * binding in this closure; `renderBody` builds the editor from `_rows`, so a virtual window
         * scrolling the row out and back, a parent toggling, or select-all would bring the old value
         * back with no marker on it — the record holding one value and the screen showing another,
         * which is the state the marker exists to make impossible.
         */
        const held = this.allRows().find((entry) => entry.id === rowId);
        if (held) {
          const cell = held.row.cells[column.key];
          if (cell != null && typeof cell === 'object') cell.text = value;
          else held.row.cells[column.key] = value;
        }
        mark();
        return;
      }
      /*
       * REFUSED, and this is the clause that makes a retry possible at all. `committed` suppresses
       * the blur that follows Enter, so after a refusal it still holds the refused value and
       * pressing Enter again would emit nothing — the person would have to change the text and
       * change it back. Forgetting the report restores the obvious behaviour: the same value can be
       * committed again, which is exactly what a host that re-read on a conflict is expecting.
       *
       * Only when the refusal is about what this cell last reported. A newer commit is still
       * awaiting its own answer and must not be forgotten on an older one's behalf.
       */
      if (committed === value) {
        committed = declared;
        refused = value;
      }
    });
    return input;
  }

  /**
   * CAOS-1672 — the row's own words, for naming a cell control inside it. The first cell holding
   * readable text is the row's subject in practice, which is the same thing the first data cell
   * already carries the disclosure for.
   *
   * It takes the ROW rather than looking one up by id, deliberately. A lookup would have been wrong
   * as well as slow: two rows may carry the same explicit `id`, and a search would then name the
   * second row's control after the first row's subject. Being handed the row the cell is being
   * drawn into cannot pick the wrong one, and it does not walk the rows once per editable cell.
   */
  private rowLabel(row: CaosTableRow): string {
    for (const col of this._columns) {
      if (col.editable === true) continue;
      const cell = row.cells[col.key];
      const text = cell == null ? '' : typeof cell === 'object' ? (cell.text ?? '') : String(cell);
      if (text !== '') return text;
    }
    return '';
  }

  private emDash(): HTMLSpanElement {
    const span = document.createElement('span');
    span.textContent = '—';
    span.className = 'em-dash';
    span.setAttribute('aria-hidden', 'false');
    return span;
  }

  private skeletonRow(): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.setAttribute('aria-hidden', 'true');
    if (this.selectable) {
      const td = document.createElement('td');
      td.className = 'selcol';
      const pad = document.createElement('div');
      pad.className = 'cellpad';
      const sk = document.createElement('caos-skeleton-block');
      sk.setAttribute('w', '18px');
      sk.setAttribute('h', '18px');
      pad.append(sk);
      td.append(pad);
      tr.append(td);
    }
    // CAOS-1671 — the gutter keeps its cell while loading, so the skeleton has exactly the cells
    // the header has and the columns do not shift when the rows arrive. It stays EMPTY rather than
    // drawing a placeholder: a position is not content on its way, and a shimmer in the gutter
    // would promise a value that was never being fetched.
    if (this.rowNumbers) {
      const td = document.createElement('td');
      td.className = 'numcol';
      const pad = document.createElement('div');
      pad.className = 'cellpad';
      td.append(pad);
      tr.append(td);
    }
    this._columns.forEach((col, i) => {
      const td = document.createElement('td');
      if (col.align) td.dataset.align = col.align;
      const pad = document.createElement('div');
      pad.className = 'cellpad';
      const sk = document.createElement('caos-skeleton-block');
      sk.setAttribute('variant', 'line');
      sk.setAttribute('w', i % 3 === 0 ? '50%' : '80%');
      pad.append(sk);
      td.append(pad);
      tr.append(td);
    });
    return tr;
  }

  /**
   * CAOS-602 — the vocabulary's `chevron-up`; the descending state rotates it 180° via CSS. The
   * heavier stroke stays, because a sort marker sits at the size of a column label.
   */
  private sortArrow(): SVGSVGElement {
    const svg = makeIcon('chevron-up');
    svg.setAttribute('stroke-width', '2.5');
    return svg;
  }

  private onSortToggle(key: string): void {
    const direction: CaosSortDirection =
      this._sort && this._sort.key === key && this._sort.direction === 'asc' ? 'desc' : 'asc';
    this._sort = { key, direction };
    this.reflectSort();
    this.dispatchEvent(
      new CustomEvent('caos-sort', { bubbles: true, composed: true, detail: { column: key, direction } }),
    );
  }

  private reflectSort(): void {
    const headers = Array.from(this.headerRow.querySelectorAll('th'));
    headers.forEach((th) => {
      const btn = th.querySelector<HTMLButtonElement>('.sort');
      if (!btn) return;
      const key = btn.dataset.key;
      if (this._sort && this._sort.key === key) {
        th.setAttribute('aria-sort', this._sort.direction === 'asc' ? 'ascending' : 'descending');
      } else {
        th.setAttribute('aria-sort', 'none');
      }
    });
  }

  /**
   * The disclosure control for one parent row. It is a real button inside the cell, so the grid's
   * own two-dimensional keyboard reaches it in order like any other focusable leaf in that row.
   */
  private rowTwisty(row: CaosTableRow, id: string, open: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'twisty';
    btn.textContent = '▾';
    const first = this._columns[0] ? row.cells[this._columns[0].key] : undefined;
    const name = typeof first === 'string' || typeof first === 'number' ? ` ${String(first)}` : '';
    btn.setAttribute('aria-label', `${open ? 'Collapse' : 'Expand'}${name}`);
    btn.addEventListener('click', () => this.toggleRow(id, !this.isOpen(id)));
    return btn;
  }

  /** Open or close one parent row. Closing HIDES its children; it deselects nothing and loses nothing. */
  private toggleRow(id: string, open: boolean): void {
    if (!this.expandable) return;
    if (open) this._collapsed.delete(id);
    else this._collapsed.add(id);
    this.renderBody();
    this.dispatchEvent(
      new CustomEvent('caos-row-expand', { bubbles: true, composed: true, detail: { id, expanded: open } }),
    );
  }

  private onRowSelect(id: string, cb: CaosCheckboxField): void {
    const found = this.allRows().find((entry) => entry.id === id);
    if (!found) return;
    const row = found.row;
    row.selected = cb.checked;
    const tr = this.tbody.querySelector<HTMLTableRowElement>(`tr[data-row-id="${CSS.escape(id)}"]`);
    tr?.setAttribute('aria-selected', String(row.selected));
    this.syncSelectAll();
    this.dispatchEvent(
      new CustomEvent('caos-row-select', {
        bubbles: true,
        composed: true,
        detail: { id, selected: row.selected, selectedIds: this.selectedIds },
      }),
    );
  }

  private onSelectAll(cb: CaosCheckboxField): void {
    const next = cb.checked;
    // Every selectable row, INCLUDING rows inside a closed parent. Somebody ticking select-all means
    // every row, not every row they happen to be looking at — and an action that quietly skipped a
    // closed branch would be wrong in the direction nobody checks.
    this.allRows().forEach(({ row }) => {
      if (!row.disabled && !row.total) row.selected = next;
    });
    // repaint the body rows' checkboxes + aria-selected
    this.renderBody();
    this.dispatchEvent(
      new CustomEvent('caos-select-all', {
        bubbles: true,
        composed: true,
        detail: { selected: next, selectedIds: this.selectedIds },
      }),
    );
  }

  private syncSelectAll(): void {
    if (!this.selectAll) return;
    const selectable = this.allRows()
      .map(({ row }) => row)
      .filter((r) => !r.disabled && !r.total);
    const selected = selectable.filter((r) => r.selected).length;
    const all = selectable.length > 0 && selected === selectable.length;
    const some = selected > 0 && !all;
    this.selectAll.indeterminate = some;
    this.selectAll.checked = all;
  }

  /**
   * Two-dimensional keyboard roving over the grid's interactive leaves. The focusable
   * stops (sortable header buttons, the select-all + row checkboxes, and any focusable
   * cell content) are enumerated PER ROW through the shared shadow-piercing util, so a
   * slotted leaf's real control — which lives in its own shadow root and is invisible to
   * a light-DOM CSS query — is reached in order. Arrow Up/Down move between rows at the
   * same column position; Arrow Left/Right within a row; Home/End to the row ends.
   */
  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;

    const grid = this.focusGrid();
    if (!grid.length) return;
    const active = deepActiveElement();
    // CAOS-1672 — a control that uses a key for itself keeps it. Grid navigation and a control in a
    // cell want the same keys, and this handler calls `preventDefault()` on all six for whatever
    // has focus, so a text box inside a cell could not move its caret and a select could not change
    // its option. That was already true of the editors hosts compose by hand today; declaring the
    // affordance makes it the ordinary case rather than an unusual one.
    if (this.controlOwnsKey(active, e.key)) return;
    const pos = this.findPos(grid, active);
    if (!pos) return;
    let { r, c } = pos;

    // CAOS-601 — a vertical move off the end of what is painted brings the window with it. The
    // repaint destroys the element that had focus, so the move is completed here rather than
    // falling through to a lookup that would no longer find anything.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const from = this.rowIndexOf(active);
      const want = from === null ? null : from + (e.key === 'ArrowDown' ? 1 : -1);
      if (want !== null && want >= 0 && want < this.visibleRows().length && this.ensureRowRendered(want)) {
        const tr = this.tbody.querySelector<HTMLElement>(`tr[data-row-index="${want}"]`);
        const cells = tr ? collectFocusables(tr) : [];
        const landing = cells[Math.max(0, Math.min(cells.length - 1, c))];
        if (landing) {
          e.preventDefault();
          landing.focus();
        }
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        r = Math.min(grid.length - 1, r + 1);
        break;
      case 'ArrowUp':
        r = Math.max(0, r - 1);
        break;
      case 'ArrowRight':
        c = c + 1;
        break;
      case 'ArrowLeft':
        c = c - 1;
        break;
      case 'Home':
        c = 0;
        break;
      case 'End':
        c = Number.MAX_SAFE_INTEGER;
        break;
    }
    const rowCells = grid[r];
    if (!rowCells || rowCells.length === 0) return;
    const target = rowCells[Math.max(0, Math.min(rowCells.length - 1, c))];
    if (target) {
      e.preventDefault();
      target.focus();
    }
  }

  /** The focusable leaves of the grid, as a per-row 2-D array (header row first). */
  private focusGrid(): HTMLElement[][] {
    const grid: HTMLElement[][] = [];
    const headerFocusables = collectFocusables(this.headerRow);
    if (headerFocusables.length) grid.push(headerFocusables);
    Array.from(this.tbody.querySelectorAll('tr')).forEach((tr) => {
      const f = collectFocusables(tr);
      if (f.length) grid.push(f);
    });
    return grid;
  }

  private findPos(grid: HTMLElement[][], active: Element | null): { r: number; c: number } | null {
    if (!active) return null;
    for (let r = 0; r < grid.length; r++) {
      const row = grid[r];
      if (!row) continue;
      const c = row.indexOf(active as HTMLElement);
      if (c >= 0) return { r, c };
    }
    return null;
  }
}

define('caos-data-table', CaosDataTable);

declare global {
  interface HTMLElementTagNameMap {
    'caos-data-table': CaosDataTable;
  }
}
